import absurd from 'common/absurd';
import * as React from 'react';
import FocusTrap from 'src/FocusTrap';
import Layer from 'src/Layer/Layer';
import {Overlay} from 'src/Layer/Overlay';
import type {FocusControls} from 'src/FocusTrap';
type Anchor = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'above-right';
type Props<RefType extends HTMLElement> = {
  children: (anchorRef: React.RefCallback<RefType>) => React.ReactNode;
  focusControlsRef?: React.MutableRefObject<FocusControls | null>;
  onOutsideClick?: () => void;
  onOutsideFocus?: () => void;
  onMouseDown?: (e: React.MouseEvent) => void;
  open: boolean;
  onContentBlur?: () => void;
  popoverContents: React.ReactNode;
  popoverAnchor?: Anchor;
};

type Position = {top: number; left: number};

const getPosition = (
  anchor: Anchor,
  triggerRect: DOMRect,
  contentRect: DOMRect,
): Position => {
  switch (anchor) {
    case 'top-left':
      return {
        top: triggerRect.top,
        left: triggerRect.left,
      };
    case 'top-right':
      return {
        top: triggerRect.bottom,
        left: triggerRect.right - contentRect.width,
      };
    case 'bottom-left':
      return {
        top: triggerRect.bottom,
        left: triggerRect.left,
      };
    case 'bottom-right':
      return {
        top: triggerRect.bottom,
        left: triggerRect.right - contentRect.width,
      };
    // Opens upward: for triggers pinned to the bottom of the screen, where a
    // menu opening downward would be clamped back up over its own trigger.
    case 'above-right':
      return {
        top: triggerRect.top - contentRect.height,
        left: triggerRect.right - contentRect.width,
      };
    default:
      return absurd(anchor);
  }
};

const Popover = <RefType extends HTMLElement>(props: Props<RefType>) => {
  const triggerRef = React.useRef<RefType | null>(null);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = React.useState<Position>({
    top: -10000,
    left: -10000,
  });

  const updatePosition = React.useCallback(() => {
    const triggerEl = triggerRef.current;
    const contentEl = contentRef.current;
    if (!triggerEl || !contentEl) return;

    const triggerRect = triggerEl.getBoundingClientRect();
    const contentRect = contentEl.getBoundingClientRect();

    const anchor = props.popoverAnchor || 'top-left';
    const nextPosition = getPosition(anchor, triggerRect, contentRect);

    const viewport = window.visualViewport;
    if (viewport) {
      const margin = 8;
      const viewportTop = viewport.offsetTop + margin;
      const viewportBottom =
        viewport.offsetTop + viewport.height - contentRect.height - margin;

      if (viewportBottom >= viewportTop) {
        nextPosition.top = Math.min(
          Math.max(nextPosition.top, viewportTop),
          viewportBottom,
        );
      }

      const viewportLeft = viewport.offsetLeft + margin;
      const viewportRight =
        viewport.offsetLeft + viewport.width - contentRect.width - margin;

      if (viewportRight >= viewportLeft) {
        nextPosition.left = Math.min(
          Math.max(nextPosition.left, viewportLeft),
          viewportRight,
        );
      }
    }

    setPosition((prev) => {
      if (prev.top === nextPosition.top && prev.left === nextPosition.left) {
        return prev;
      }
      return nextPosition;
    });
  }, [props.popoverAnchor]);

  React.useEffect(() => {
    if (!props.open) return;
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', updatePosition);
    viewport?.addEventListener('scroll', updatePosition);

    // Contents that grow after opening (e.g. the date filter revealing its
    // custom-range inputs, which widen the menu well past the option list)
    // would otherwise keep the position measured for the smaller box and hang
    // off the edge of a narrow viewport.
    const observer = new ResizeObserver(updatePosition);
    if (contentRef.current) {
      observer.observe(contentRef.current);
    }

    // None of the above fires when the TRIGGER moves without a resize or a
    // scroll — e.g. FilterPillRow floats a pill to the front the moment its
    // value lands, mid-open for menus that stay up (multi-selects, typed
    // bounds committed on blur). Poll the rects while open so the menu
    // follows its trigger; updatePosition bails out of the state update when
    // nothing moved, so an idle frame costs two rect reads and no render.
    let frame = 0;
    const follow = () => {
      updatePosition();
      frame = requestAnimationFrame(follow);
    };
    frame = requestAnimationFrame(follow);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      viewport?.removeEventListener('resize', updatePosition);
      viewport?.removeEventListener('scroll', updatePosition);
    };
  }, [props.open, updatePosition]);

  const anchorRef = (element: RefType | null) => {
    if (element && triggerRef.current !== element) {
      triggerRef.current = element;
      updatePosition();
    }
  };

  return (
    <>
      {props.children(anchorRef)}
      {position && props.open && (
        <Layer>
          <Overlay onOverlayClick={props.onOutsideClick}>
            <div
              className="fixed"
              style={{
                top: `${position.top}px`,
                left: `${position.left}px`,
              }}
              onBlur={props.onContentBlur}
            >
              <FocusTrap
                behavior="continue"
                onFocusOutside={props.onOutsideFocus}
                focusControlsRef={props.focusControlsRef}
              >
                <div ref={contentRef}>{props.popoverContents}</div>
              </FocusTrap>
            </div>
          </Overlay>
        </Layer>
      )}
    </>
  );
};

export default Popover;
